import { useEffect, useMemo, useRef, useState } from 'react'
import { toast } from 'sonner'
import {
  ArrowDown,
  ArrowUp,
  ChevronsUpDown,
  Columns3,
  Download,
  Maximize2,
  Minimize2,
  Pin,
  Rows3,
  Search,
  Filter as FilterIcon,
  X,
} from 'lucide-react'
import { Pagination } from '../ui/Pagination'
import { Button } from '../ui/Button'
import { rangeIdsBetween, applyRange } from '../../lib/useRangeSelection'
import {
  DENSITIES,
  DENSITY_PAD,
  useStickyPins,
  useTableColumns,
} from './tableColumns'
import { ColumnManager, ExportModal, FilterDrawer } from './tableOverlays'
import { FloatingXScrollbar } from './FloatingXScrollbar'
import { countFilters, emptyFilterValues } from './tableFilters'
import { runExport } from './tableExport'
import { useDisplayPref, usePageSize } from '../../lib/display-prefs'
// Pure filter → search → sort pipeline (extracted so it's unit-testable and
// this file exports only its component).
import { matchesFilters, matchesSearch, multiSort } from './tablePipeline'
import { formatNumber, todayKey } from '../../lib/format'

/* ============================ toolbar button ============================ */

function ToolButton({ icon: Icon, label, badge, active, onClick, ...rest }) {
  return (
    <button
      type="button"
      onClick={onClick}
      title={label}
      aria-label={label}
      className={
        'relative inline-flex items-center gap-1.5 h-9 px-3 rounded-lg border text-sm font-medium cursor-pointer transition-colors ' +
        (active
          ? 'border-[color:var(--color-accent)] bg-[color:var(--color-accent-bg)] text-[color:var(--color-accent-strong)]'
          : 'border-[color:var(--color-rule)] bg-[color:var(--color-card)] text-[color:var(--color-ink)] hover:bg-[color:var(--color-card-hover)]')
      }
      {...rest}
    >
      <Icon size={15} />
      <span className="hidden sm:inline">{label}</span>
      {badge ? (
        <span className="inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full bg-[color:var(--color-accent)] text-white text-[11px] font-semibold tabular-nums">
          {badge}
        </span>
      ) : null}
    </button>
  )
}

/* ============================ header sort control ============================ */

function HeaderSort({ col, sort, onSort }) {
  const idx = sort.findIndex((s) => s.key === col.key)
  const state = idx > -1 ? sort[idx].dir : 'none'
  const Icon = state === 'asc' ? ArrowUp : state === 'desc' ? ArrowDown : ChevronsUpDown
  return (
    <button
      type="button"
      onClick={(e) => onSort(col.key, e.shiftKey)}
      aria-label={`Sort by ${col.header}`}
      className={
        // 24px minimum target height (WCAG 2.5.8) — header text is unchanged,
        // the clickable box just reaches the full 24px.
        'inline-flex min-h-[24px] items-center gap-1 rounded px-1 -mx-1 py-0.5 cursor-pointer transition-colors ' +
        (state !== 'none'
          ? 'text-[color:var(--color-accent-strong)]'
          : 'text-[color:var(--color-ink-muted)] hover:text-[color:var(--color-ink-strong)]')
      }
    >
      <span>{col.header}</span>
      <Icon size={13} className={state === 'none' ? 'opacity-50' : ''} />
      {sort.length > 1 && idx > -1 ? (
        <span className="text-[10px] font-semibold tabular-nums">{idx + 1}</span>
      ) : null}
    </button>
  )
}

/* ============================ main component ============================ */

export function DataTable({
  tableId,
  columns,
  rows,
  getRowId = (r) => r.id,
  // Optional per-row props merged onto each <tr> — `{ className?, ...attrs }`.
  // Lets a host page attach a data-* hook or a transient highlight class (e.g.
  // a deep-link focus ring) without the table owning that concern.
  getRowProps,

  loading = false,
  fetching = false,
  error = null,
  onRetry,

  search: searchProp,
  onSearchChange,
  searchScopes,
  searchPlaceholder = 'Search',
  // Set false when the host page owns search elsewhere (e.g. a shared
  // FilterRow above a dual list/grid view) so DataTable doesn't render a
  // second, duplicate search box.
  searchable = true,

  filterConfig,
  filterValues: filterValuesProp,
  onFilterChange,
  filterPreviewCount,

  defaultSort = [],
  // Server-sorted tables: called with the sort list (`[{ key, dir }]`, empty
  // = the server's default order) whenever it changes. When given, rows are
  // shown in the order they arrive — sorting one page locally would order a
  // page, not the list.
  onSortChange,

  page: pageProp,
  totalPages: totalPagesProp,
  total: totalProp,
  limit: limitProp,
  onPageChange,
  onLimitChange,
  // Server mode: the page sizes this list's API accepts (from the page's
  // `usePageSize`). Omitted → Pagination's standard 25…500.
  limitOptions,

  selectable = false,
  bulkActions = [],
  onSelectAllRecords,

  exportBaseName = 'export',
  exportTitle,
  isoDate,
  getExportRows,

  emptyState,
  title,
}) {
  const hasFilters = Array.isArray(filterConfig) && filterConfig.length > 0
  const hasScopes = Array.isArray(searchScopes) && searchScopes.length > 1
  const serverPaginated = totalProp != null
  const accountRowDensity = useDisplayPref('rowDensity')

  const {
    arranged,
    visibleColumns,
    density,
    setOrder,
    setHidden,
    togglePinned,
    setDensity,
    reset: resetColumns,
  } = useTableColumns(tableId, columns, {
    // The person's account row height — used unless this table has its own
    // explicit choice from the density menu.
    defaultDensity: accountRowDensity,
  })

  /* ---- client pagination state (declared early so the uncontrolled
   *  search / filter setters can reset the page) ---- */
  const [clientPage, setClientPage] = useState(1)
  // Starts at (and follows) the person's account rows-per-page until they
  // pick a size here. Server-paginated tables get their size from the page,
  // which uses the same `usePageSize` hook.
  const [clientLimit, setClientLimit] = usePageSize()

  /* ---- controlled-or-internal search / filters ---- */
  const searchControlled = typeof onSearchChange === 'function'
  const [internalSearch, setInternalSearch] = useState('')
  const search = searchControlled ? searchProp ?? '' : internalSearch
  // Uncontrolled (client) mode also snaps back to page 1 on every change,
  // so a narrowing search never leaves you stranded on an empty page.
  const setSearch = searchControlled
    ? onSearchChange
    : (v) => {
        setInternalSearch(v)
        setClientPage(1)
      }
  const [scope, setScopeState] = useState(searchScopes?.[0]?.key ?? 'all')
  const setScope = (v) => {
    setScopeState(v)
    if (!searchControlled) setClientPage(1)
  }

  const filtersControlled = typeof onFilterChange === 'function'
  const [internalFilters, setInternalFilters] = useState(() =>
    emptyFilterValues(filterConfig || []),
  )
  const filterValues = filtersControlled
    ? filterValuesProp ?? emptyFilterValues(filterConfig || [])
    : internalFilters
  const setFilterValues = filtersControlled
    ? onFilterChange
    : (v) => {
        setInternalFilters(v)
        setClientPage(1)
      }

  const [sort, setSort] = useState(defaultSort)
  const serverSorted = typeof onSortChange === 'function'
  const onSortChangeRef = useRef(onSortChange)
  useEffect(() => {
    onSortChangeRef.current = onSortChange
  })
  const firstSortRef = useRef(true)
  useEffect(() => {
    // Skip the mount: the host already holds the default it passed in.
    if (firstSortRef.current) {
      firstSortRef.current = false
      return
    }
    onSortChangeRef.current?.(sort)
  }, [sort])
  const [selected, setSelected] = useState(() => new Set())

  /* ---- overlays ---- */
  const [drawerOpen, setDrawerOpen] = useState(false)
  const [exportOpen, setExportOpen] = useState(false)
  const [colsOpen, setColsOpen] = useState(false)
  const [colsRect, setColsRect] = useState(null)
  const [fullscreen, setFullscreen] = useState(false)
  const [densityMenuOpen, setDensityMenuOpen] = useState(false)

  const scrollRef = useRef(null)

  /* ---- pipeline ---- */
  const processed = useMemo(() => {
    let r = rows || []
    if (!serverPaginated) {
      if (hasFilters)
        r = r.filter((row) => matchesFilters(row, filterConfig, filterValues))
      if (searchable)
        r = r.filter((row) =>
          matchesSearch(row, search, scope, searchScopes, columns),
        )
    }
    return serverSorted ? r : multiSort(r, sort, columns)
  }, [
    rows,
    serverSorted,
    serverPaginated,
    hasFilters,
    filterConfig,
    filterValues,
    searchable,
    search,
    scope,
    searchScopes,
    columns,
    sort,
  ])

  const total = serverPaginated ? totalProp : processed.length
  const limit = serverPaginated ? limitProp : clientLimit
  const page = serverPaginated ? pageProp : clientPage
  const totalPages = serverPaginated
    ? totalPagesProp
    : Math.max(1, Math.ceil(processed.length / clientLimit))
  const pageRows = serverPaginated
    ? processed
    : processed.slice((clientPage - 1) * clientLimit, clientPage * clientLimit)

  const changePage = serverPaginated ? onPageChange : setClientPage
  const changeLimit = serverPaginated
    ? onLimitChange
    : (n) => {
        setClientLimit(n)
        setClientPage(1)
      }

  /* ---- selection ---- */
  const pageIds = useMemo(() => pageRows.map(getRowId), [pageRows, getRowId])
  const allPageSelected =
    pageIds.length > 0 && pageIds.every((id) => selected.has(id))
  const somePageSelected = pageIds.some((id) => selected.has(id))

  const selectAnchorRef = useRef(null)
  function toggleRow(id) {
    setSelected((s) => {
      const n = new Set(s)
      n.has(id) ? n.delete(id) : n.add(id)
      return n
    })
  }
  /* Row selection is driven ENTIRELY from the checkbox's onClick (which is the
     only handler that carries the native shiftKey flag); onChange is a no-op.
     Doing both the single-toggle and the range here avoids the double-toggle
     bug where a split onClick(range) + onChange(toggle) cancelled the clicked
     row — leaving the Shift+clicked row unselected. */
  function handleRowSelectClick(e, id) {
    if (e.shiftKey && selectAnchorRef.current != null) {
      const ids = rangeIdsBetween(pageIds, selectAnchorRef.current, id)
      if (ids.length > 1) {
        // NB: no `e.preventDefault()` here. This is a CONTROLLED checkbox
        // (`checked={isSel}` + no-op onChange); calling preventDefault on the
        // clicked checkbox left React's controlled value out of sync so the
        // shift-clicked row itself rendered unchecked (the "last row not
        // included" bug). Our range logic sets the clicked row to exactly what
        // the native toggle would, so letting the default run stays consistent.
        const shouldSelect = !selected.has(id)
        setSelected((s) => applyRange(s, ids, shouldSelect))
        selectAnchorRef.current = id
        return
      }
    }
    // Plain click (or Shift with no resolvable range): toggle just this row.
    toggleRow(id)
    selectAnchorRef.current = id
  }
  function toggleAllPage() {
    setSelected((s) => {
      const n = new Set(s)
      if (allPageSelected) pageIds.forEach((id) => n.delete(id))
      else pageIds.forEach((id) => n.add(id))
      return n
    })
  }
  function clearSelection() {
    setSelected(new Set())
  }
  async function selectAllRecords() {
    if (onSelectAllRecords) {
      const ids = await onSelectAllRecords()
      setSelected(new Set(ids))
    } else {
      setSelected(new Set(processed.map(getRowId)))
    }
    toast.success('All records selected.')
  }

  /* ---- sorting ---- */
  function onSort(key, shift) {
    setSort((prev) => {
      const idx = prev.findIndex((s) => s.key === key)
      // Server-sorted: one key only (the server sorts by a single field, so
      // Shift-click would add arrows and chips that change nothing), and a
      // click on the sorted column flips its direction instead of clearing
      // it — clearing would fall back to the server default, which for the
      // default column looks like a click that did nothing. The chip's ✕
      // still clears.
      if (serverSorted) {
        if (idx === -1) return [{ key, dir: 'asc' }]
        return [{ key, dir: prev[idx].dir === 'asc' ? 'desc' : 'asc' }]
      }
      if (!shift) {
        if (idx === -1) return [{ key, dir: 'asc' }]
        if (prev[idx].dir === 'asc') return [{ key, dir: 'desc' }]
        return []
      }
      const next = prev.map((s) => ({ ...s }))
      if (idx === -1) {
        if (next.length < 3) next.push({ key, dir: 'asc' })
        else toast.message('Up to three sort columns.')
        return next
      }
      if (next[idx].dir === 'asc') {
        next[idx].dir = 'desc'
        return next
      }
      return next.filter((s) => s.key !== key)
    })
  }

  /* ---- pins (enforce max 3 frozen data columns) ---- */
  function onTogglePin(key) {
    const pinnedCount = arranged.filter((c) => c.pinned).length
    const col = arranged.find((c) => c.key === key)
    if (col && !col.pinned && pinnedCount >= 3) {
      toast.message('Up to three frozen columns.')
      return
    }
    togglePinned(key)
  }

  /* ---- chips ---- */
  const chips = useMemo(() => {
    const out = []
    if (hasFilters) {
      for (const f of filterConfig) {
        const v = filterValues[f.key]
        if (f.type === 'checkbox' && Array.isArray(v)) {
          v.forEach((val) => {
            const opt = f.options.find((o) => o.value === val)
            out.push({
              key: `f:${f.key}:${val}`,
              label: f.label,
              value: opt?.label ?? val,
              clear: () =>
                setFilterValues({
                  ...filterValues,
                  [f.key]: v.filter((x) => x !== val),
                }),
            })
          })
        } else if ((f.type === 'radio' || f.type === 'select') && v) {
          const opt = f.options.find((o) => o.value === v)
          out.push({
            key: `f:${f.key}`,
            label: f.label,
            value: opt?.label ?? v,
            clear: () => setFilterValues({ ...filterValues, [f.key]: '' }),
          })
        } else if (f.type === 'daterange' && v && (v.from || v.to)) {
          out.push({
            key: `f:${f.key}`,
            label: f.label,
            value: `${v.from || '…'} → ${v.to || '…'}`,
            clear: () =>
              setFilterValues({ ...filterValues, [f.key]: { from: '', to: '' } }),
          })
        } else if (f.type === 'text' && v) {
          out.push({
            key: `f:${f.key}`,
            label: f.label,
            value: v,
            clear: () => setFilterValues({ ...filterValues, [f.key]: '' }),
          })
        }
      }
    }
    sort.forEach((s) => {
      const c = columns.find((c) => c.key === s.key)
      out.push({
        key: `s:${s.key}`,
        label: 'Sorted',
        value: `${c?.header ?? s.key} ${s.dir === 'asc' ? '↑' : '↓'}`,
        clear: () => setSort((p) => p.filter((x) => x.key !== s.key)),
      })
    })
    if (searchable && (search || '').trim().length >= 2) {
      const sc = searchScopes?.find((x) => x.key === scope)
      out.push({
        key: 'q',
        label: `${sc?.label ?? 'Search'}`,
        value: `“${search.trim()}”`,
        clear: () => setSearch(''),
      })
    }
    return out
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasFilters, filterConfig, filterValues, sort, columns, search, scope, searchScopes])

  const filterCount = hasFilters ? countFilters(filterConfig, filterValues) : 0

  function clearAll() {
    if (hasFilters) setFilterValues(emptyFilterValues(filterConfig))
    setSort([])
    setSearch('')
  }

  /* ---- export ---- */
  async function handleExport({ scope: exScope, columns: colScope, format }) {
    const source = colScope === 'all' ? arranged : visibleColumns
    const exportCols = source.filter((c) => c.exportable !== false)
    let data
    let partial = false
    if (exScope === 'page') data = pageRows
    else if (exScope === 'selected')
      data = getExportRows
        ? await getExportRows('selected', [...selected])
        : pageRows.filter((r) => selected.has(getRowId(r)))
    else {
      // all records
      if (getExportRows) data = await getExportRows('all')
      else if (serverPaginated) {
        data = pageRows
        partial = true
      } else data = processed
    }
    const stamp = isoDate || todayKey()
    const res = runExport(format, data, exportCols, {
      baseName: exportBaseName,
      title: exportTitle || title || exportBaseName,
      isoDate: stamp,
    })
    setExportOpen(false)
    if (res.unsupported)
      toast.error(`${res.format} export isn’t available — pick another format.`)
    else if (partial)
      toast.message(
        `Exported the current page only (${formatNumber(data.length)} records). Full-set export isn’t wired for this table yet.`,
      )
    else if (res.downloaded)
      toast.success(`Exported ${formatNumber(data.length)} records.`)
  }

  /* ---- fullscreen escape ---- */
  useEffect(() => {
    if (!fullscreen) return
    const onKey = (e) => {
      if (e.key === 'Escape') setFullscreen(false)
    }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [fullscreen])

  /* ---- pin layout ---- */
  useStickyPins(scrollRef, [
    visibleColumns,
    pageRows.length,
    density,
    fullscreen,
    selectable,
  ])

  /* ---- render pieces ---- */
  const pad = DENSITY_PAD[density] || DENSITY_PAD.comfortable
  // `text-left` is NOT in the base: a column with `align: 'center'` produced
  // `class="… text-left … text-center"`, and with two Tailwind utilities of
  // equal specificity on one element the winner is decided by their order in
  // the generated STYLESHEET, not by the order in the class attribute — so
  // `text-left` won and every centered column had a left-aligned header over
  // centered cells (QA-094). The `<td>` had no such conflict, which is why
  // only the headers drifted. It is applied conditionally below instead.
  const thBase = `px-4 ${pad} border-b-2 border-[color:var(--color-rule)] bg-[color:var(--color-canvas)] align-middle font-medium text-sm text-[color:var(--color-ink-muted)] whitespace-nowrap`
  const tdBase = `px-4 ${pad} align-middle border-b border-[color:var(--color-rule)] text-sm`
  // Leading control columns (select-all checkbox + Sr. No.) get tighter
  // horizontal padding so they stay as narrow as their content.
  const thLead = thBase.replace('px-4', 'px-2.5')
  const tdLead = tdBase.replace('px-4', 'px-2.5')

  const rightPinned = visibleColumns.filter((c) => c.pinnedRight)
  const mainCols = visibleColumns.filter((c) => !c.pinnedRight)
  const leftPinned = mainCols.filter((c) => c.pinned)
  // Index of the last frozen-left cell (control cells + left-pinned cols).
  const nLead = (selectable ? 1 : 0) + 1 // checkbox? + serial

  const minWidth = useMemo(() => {
    // Lead columns are narrow: ~40px checkbox + ~56px Sr. No.
    let w = (selectable ? 40 : 0) + 56
    for (const c of mainCols) w += c.minWidth || 140
    for (const c of rightPinned) w += c.minWidth || 120
    return w
  }, [mainCols, rightPinned, selectable])

  // When the table is wider than its content, the slack has to land
  // somewhere. Prefer a column flagged `grow` (e.g. the primary Name
  // column) so it widens naturally; otherwise drop in a flexible spacer
  // before the right-pinned Actions so no real column — least of all a
  // narrow Actions kebab — gets stretched to fill the gap.
  const growKey = mainCols.find((c) => c.grow)?.key
  const needSpacer = !growKey

  const cx = (...a) => a.filter(Boolean).join(' ')

  const table = (
    <>
    {/* Card wrapper — border + rounded corners + shadow. The horizontal
     *  scroll lives on the INNER `.dt-scroll` div (rows only), so the
     *  pagination footer below it stays put when the user scrolls the
     *  table sideways instead of sliding off to the right. */}
    <div className="rounded-xl border border-[color:var(--color-rule)] bg-[color:var(--color-card)] shadow-sm overflow-hidden">
      <div
        ref={scrollRef}
        data-fd-xscroll
        className="dt-scroll overflow-x-auto bg-[color:var(--color-card)]"
      >
      <table
        className="w-full text-left border-separate border-spacing-0"
        style={{ minWidth: `${minWidth}px` }}
      >
        <thead>
          <tr>
            {selectable ? (
              <th data-pin-left className={cx(thLead, 'w-10')}>
                <input
                  type="checkbox"
                  aria-label="Select all rows on this page"
                  checked={allPageSelected}
                  ref={(el) => {
                    if (el) el.indeterminate = somePageSelected && !allPageSelected
                  }}
                  onChange={toggleAllPage}
                  className="cursor-pointer"
                />
              </th>
            ) : null}
            <th
              data-pin-left
              data-pin-edge={leftPinned.length === 0 ? '' : undefined}
              className={cx(thLead, 'w-12')}
            >
              SR. No.
            </th>
            {mainCols.map((c) => {
              return (
                <th
                  key={c.key}
                  data-pin-left={c.pinned ? '' : undefined}
                  data-pin-edge={c.pinned && c.key === leftPinned.at(-1)?.key ? '' : undefined}
                  className={cx(
                    thBase,
                    c.align === 'center' && 'text-center',
                    c.align === 'right' && 'text-right',
                    // Explicit default — see the note on `thBase`.
                    c.align !== 'center' && c.align !== 'right' && 'text-left',
                  )}
                  style={{
                    minWidth: c.minWidth ? `${c.minWidth}px` : undefined,
                    width: c.grow ? '100%' : undefined,
                  }}
                  // Optional hover definition of what the column measures.
                  title={c.headerTitle || undefined}
                >
                  <span
                    className={cx(
                      'inline-flex items-center gap-1',
                      c.align === 'center' && 'justify-center',
                      // The header content is an inline-flex row, so a right
                      // aligned column needs the row pushed over too — the
                      // text-right on the cell alone does not move it.
                      c.align === 'right' && 'justify-end',
                    )}
                  >
                    {c.sortable !== false && c.sortableHeader !== false ? (
                      <HeaderSort col={c} sort={sort} onSort={onSort} />
                    ) : (
                      <span>{c.header}</span>
                    )}
                    {c.pinnable ? (
                      <button
                        type="button"
                        onClick={() => onTogglePin(c.key)}
                        title={c.pinned ? 'Unfreeze column' : 'Freeze column'}
                        aria-pressed={c.pinned}
                        className={
                          // 24x24 minimum hit area (WCAG 2.5.8).
                          'inline-flex min-h-[24px] min-w-[24px] items-center justify-center rounded p-0.5 transition-colors ' +
                          (c.pinned
                            ? 'text-[color:var(--color-accent-strong)]'
                            : 'text-[color:var(--color-ink-subtle)] hover:text-[color:var(--color-ink)]')
                        }
                      >
                        <Pin size={12} />
                      </button>
                    ) : null}
                  </span>
                  {/* Optional second header line — a plain STRING, shown on
                   *  screen only. The column's name stays `header` (one line),
                   *  which is what the sort control, the column manager and
                   *  every export read. */}
                  {c.subheader ? (
                    <span className="block text-[11px] font-normal leading-tight text-[color:var(--color-ink-subtle)]">
                      {c.subheader}
                    </span>
                  ) : null}
                </th>
              )
            })}
            {needSpacer ? (
              <th
                aria-hidden="true"
                className={cx(thBase, 'p-0')}
                style={{ width: '100%' }}
              />
            ) : null}
            {rightPinned.map((c) => (
              <th
                key={c.key}
                data-pin-right
                className={cx(
                  thBase,
                  c.align === 'center' && 'text-center',
                  c.align === 'right' && 'text-right',
                )}
                style={{ minWidth: c.minWidth ? `${c.minWidth}px` : undefined }}
                title={c.headerTitle || undefined}
              >
                {c.header}
              </th>
            ))}
          </tr>
        </thead>
        <tbody className={fetching ? 'opacity-60 transition-opacity' : ''}>
          {pageRows.map((row, idx) => {
            const id = getRowId(row)
            const serial = (page - 1) * limit + idx + 1
            const isSel = selected.has(id)
            const { className: rowClassName, ...rowRest } =
              getRowProps?.(row) ?? {}
            return (
              <tr
                key={id}
                {...rowRest}
                className={cx(
                  'group transition-colors hover:bg-[color:var(--color-canvas)]',
                  isSel && 'bg-[color:var(--color-accent-bg)]',
                  rowClassName,
                )}
              >
                {selectable ? (
                  <td data-pin-left className={cx(tdLead)}>
                    <input
                      type="checkbox"
                      aria-label={`Select row ${serial}`}
                      checked={isSel}
                      onClick={(e) => handleRowSelectClick(e, id)}
                      onChange={() => {}}
                      title="Tip: Shift+Click to select a range"
                      className="cursor-pointer"
                    />
                  </td>
                ) : null}
                <td
                  data-pin-left
                  data-pin-edge={leftPinned.length === 0 ? '' : undefined}
                  className={cx(
                    tdLead,
                    'text-[color:var(--color-ink-muted)] tabular-nums',
                  )}
                >
                  {serial}
                </td>
                {mainCols.map((c) => (
                  <td
                    key={c.key}
                    data-pin-left={c.pinned ? '' : undefined}
                    data-pin-edge={c.key === leftPinned.at(-1)?.key ? '' : undefined}
                    className={cx(
                      tdBase,
                      c.align === 'center' && 'text-center',
                      c.align === 'right' && 'text-right',
                      c.cellClassName,
                    )}
                  >
                    {c.render ? c.render(row, { serial }) : formatCell(row[c.key])}
                  </td>
                ))}
                {needSpacer ? (
                  <td
                    aria-hidden="true"
                    className="border-b border-[color:var(--color-rule)] p-0"
                  />
                ) : null}
                {rightPinned.map((c) => (
                  <td
                    key={c.key}
                    data-pin-right
                    className={cx(
                      tdBase,
                      c.align === 'center' && 'text-center',
                      c.align === 'right' && 'text-right',
                      c.cellClassName,
                    )}
                  >
                    {c.render ? c.render(row, { serial }) : formatCell(row[c.key])}
                  </td>
                ))}
              </tr>
            )
          })}
        </tbody>
      </table>
      </div>
      {total > 0 ? (
        <Pagination
          page={page}
          totalPages={totalPages}
          total={total}
          limit={limit}
          loading={fetching}
          onPageChange={changePage}
          onLimitChange={changeLimit}
          limitOptions={serverPaginated ? limitOptions : undefined}
        />
      ) : null}
    </div>
    {/* In full-screen the table sits on a z-60 overlay, so lift the bar above
     *  it (but below menus/modals at z-65+); normal mode keeps the CSS default. */}
    <FloatingXScrollbar containerRef={scrollRef} zIndex={fullscreen ? 61 : undefined} />
    </>
  )

  // Export tooltip — says, in a sentence, how many records the reader is
  // about to pull. The modal shows the same counts, but only once you are
  // already inside it, so the number that decides whether to click at all
  // was invisible until after the click (QA).
  //
  // Phrased as prose rather than a list of counts: the first version read
  // "Export — 3 selected, 25 on this page, 128 in total", which is the same
  // information but scans as a data dump rather than a sentence. Mirrors the
  // wording of TaskMonitorPage's bespoke `CsvExportButton`, which has read
  // "Export N tasks … as CSV" for a while.
  //
  // The alternatives are spelled out because the modal lets the reader CHOOSE
  // the scope; naming only the total would misstate the default, which is the
  // current page.
  const exportTooltip = (() => {
    const n = (x) => formatNumber(x)
    const records = (x) => `${n(x)} record${x === 1 ? '' : 's'}`
    if (total === 0) return 'Nothing to export'
    const page = pageRows.length
    const sel = selected.size
    // A selection that already covers every record offers no choice over
    // "all", so it falls through to the plain wording rather than saying
    // "the record you selected, 1 on this page, or all 1".
    if (sel > 0 && sel !== total) {
      // "the record" reads better than "the 1 record" for a single row; the
      // count only earns its place once there is more than one.
      const mine = sel === 1 ? 'the record' : `the ${n(sel)} records`
      return total === page
        ? `Export ${mine} you selected, or all ${n(total)}`
        : `Export ${mine} you selected, ${n(page)} on this page, or all ${n(total)}`
    }
    // One page holds everything, so there is no choice to spell out.
    if (total === page) return `Export ${records(total)}`
    return `Export ${records(page)} on this page, or all ${n(total)}`
  })()

  const body = (
    <div className={fullscreen ? 'fixed inset-0 z-[60] bg-[color:var(--color-canvas)] p-4 overflow-auto' : ''}>
      {/* Toolbar */}
      <div className="flex flex-wrap items-center gap-2 mb-3">
        {/* Search — same `fd-search-pill` recipe as the app-wide
         *  FilterRow.Search (fully-rounded navy-hairline pill, leading
         *  magnifier, trailing ✕) so every table's search bar reads
         *  identically whether the page uses DataTable's own search or a
         *  shared FilterRow. When an optional scope selector is present it
         *  segments onto the pill's left edge. */}
        {searchable ? (
          <div className="flex items-center">
            {hasScopes ? (
              <select
                aria-label="Search scope"
                value={scope}
                onChange={(e) => setScope(e.target.value)}
                className="h-9 rounded-l-full border border-r-0 border-[color:var(--color-rule)] bg-[color:var(--color-card)] pl-3 pr-6 text-xs text-[color:var(--color-ink-muted)] outline-none cursor-pointer appearance-none"
              >
                {searchScopes.map((s) => (
                  <option key={s.key} value={s.key}>
                    {s.label}
                  </option>
                ))}
              </select>
            ) : null}
            <div
              className={cx(
                'relative max-w-full',
                hasScopes ? 'w-56 sm:w-72' : 'w-full sm:w-96',
              )}
            >
              <span
                aria-hidden="true"
                className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 z-10 text-[color:var(--color-ink-muted)]"
              >
                <Search size={15} />
              </span>
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                // Capped to match the API’s `search` param (max 200).
                maxLength={200}
                placeholder={searchPlaceholder}
                className={cx(
                  // Keyboard-focus ring comes from the shared `.fd-search-pill`
                  // recipe (index.css) so every search pill in the app behaves
                  // the same — see QA-244 / D-A11Y-002.
                  'fd-search-pill w-full h-9 pl-9 pr-9 text-sm text-[color:var(--color-ink)] placeholder:text-[color:var(--color-ink-muted)] outline-none',
                  hasScopes ? 'rounded-r-full' : 'rounded-full',
                )}
              />
              {search ? (
                <button
                  type="button"
                  onClick={() => setSearch('')}
                  aria-label="Clear search"
                  className="absolute right-2 top-1/2 -translate-y-1/2 p-1 rounded text-[color:var(--color-ink-muted)] hover:text-[color:var(--color-ink-strong)] hover:bg-[color:var(--color-card-hover)] cursor-pointer"
                >
                  <X size={12} />
                </button>
              ) : null}
            </div>
          </div>
        ) : null}

        <div className="flex-1" />

        {hasFilters ? (
          <ToolButton
            icon={FilterIcon}
            label="Filters"
            badge={filterCount || undefined}
            active={filterCount > 0}
            onClick={() => setDrawerOpen(true)}
          />
        ) : null}

        {/* Density */}
        <div className="relative">
          <ToolButton
            icon={Rows3}
            label="Density"
            active={densityMenuOpen}
            onClick={() => setDensityMenuOpen((o) => !o)}
          />
          {densityMenuOpen ? (
            <>
              <div
                className="fixed inset-0 z-[65]"
                onClick={() => setDensityMenuOpen(false)}
              />
              <div className="absolute right-0 z-[66] mt-1 w-40 rounded-lg border border-[color:var(--color-rule)] bg-[color:var(--color-card)] shadow-lg py-1">
                {DENSITIES.map((d) => (
                  <button
                    key={d}
                    type="button"
                    onClick={() => {
                      setDensity(d)
                      setDensityMenuOpen(false)
                    }}
                    className={cx(
                      'flex w-full items-center justify-between px-3 py-1.5 text-sm cursor-pointer hover:bg-[color:var(--color-card-hover)] capitalize',
                      d === density
                        ? 'text-[color:var(--color-accent-strong)] font-medium'
                        : 'text-[color:var(--color-ink)]',
                    )}
                  >
                    {d}
                    {d === density ? <span>✓</span> : null}
                  </button>
                ))}
              </div>
            </>
          ) : null}
        </div>

        <ToolButton
          icon={fullscreen ? Minimize2 : Maximize2}
          label={fullscreen ? 'Exit full screen' : 'Full screen'}
          onClick={() => setFullscreen((f) => !f)}
        />
        <ToolButton
          icon={Download}
          label="Export"
          title={exportTooltip}
          onClick={() => setExportOpen(true)}
        />
        <ToolButton
          icon={Columns3}
          label="Columns"
          onClick={(e) => {
            setColsRect(e.currentTarget.getBoundingClientRect())
            setColsOpen(true)
          }}
        />
      </div>

      {/* Applied chips */}
      {chips.length > 0 ? (
        <div className="flex flex-wrap items-center gap-2 mb-3">
          <span className="text-xs font-medium text-[color:var(--color-ink-muted)]">
            Applied
          </span>
          {chips.map((c) => (
            <span
              key={c.key}
              className="inline-flex items-center gap-1.5 h-7 pl-2.5 pr-1.5 rounded-full border border-[color:var(--color-rule)] bg-[color:var(--color-card)] text-xs text-[color:var(--color-ink)]"
            >
              <span className="text-[color:var(--color-ink-muted)]">{c.label}</span>
              <span className="font-medium">{c.value}</span>
              <button
                type="button"
                onClick={c.clear}
                aria-label={`Remove ${c.label} ${c.value}`}
                // 24x24 minimum hit area (WCAG 2.5.8) — the glyph stays 12px,
                // the target grows around it via min-w/min-h + centring.
                className="inline-flex min-h-[24px] min-w-[24px] items-center justify-center rounded-full p-0.5 text-[color:var(--color-ink-muted)] hover:bg-[color:var(--color-card-hover)] hover:text-[color:var(--color-ink)] cursor-pointer"
              >
                <X size={12} />
              </button>
            </span>
          ))}
          <button
            type="button"
            onClick={clearAll}
            className="text-xs font-medium text-[color:var(--color-accent-strong)] hover:underline cursor-pointer"
          >
            Clear All
          </button>
        </div>
      ) : null}

      {/* Bulk bar — matches the shared pattern on Notifications / Files:
         neutral card, shared <Button>, "Select all (N)" + "Deselect all". */}
      {selectable && selected.size > 0 ? (
        <div className="flex flex-wrap items-center gap-3 mb-3 px-4 py-2.5 rounded-lg border border-[color:var(--color-rule)] bg-[color:var(--color-card)]">
          <span className="text-sm font-medium text-[color:var(--color-ink-strong)] tabular-nums">
            {formatNumber(selected.size)} selected
          </span>
          {selected.size < total ? (
            <Button size="sm" variant="secondary" onClick={selectAllRecords}>
              Select all ({formatNumber(total)})
            </Button>
          ) : null}
          <Button size="sm" variant="secondary" onClick={clearSelection}>
            <X size={14} /> Deselect All
          </Button>
          {/* Export the selection.
           *
           * Selecting rows on a table with no `bulkActions` left a bar that
           * only counted them, so the selection looked inert (defect DF_027 /
           * Mantis 22207). It never was: the export modal already offers a
           * "Selected records" scope driven by this same set. That capability
           * was simply undiscoverable from here. Every DataTable carries the
           * Export tool, so this is unconditional; it sits before the page's
           * own bulk actions so a destructive one still reads last. */}
          <Button size="sm" variant="secondary" onClick={() => setExportOpen(true)}>
            <Download size={14} /> Export selected
          </Button>
          {bulkActions.map((a) => (
            <Button
              key={a.label}
              size="sm"
              variant={a.danger ? 'danger' : 'secondary'}
              onClick={() => a.onClick([...selected], { clearSelection })}
            >
              {a.icon ? <a.icon size={14} /> : null}
              {a.label}
            </Button>
          ))}
        </div>
      ) : null}

      {/* States */}
      {loading ? (
        <SkeletonTable rows={6} cols={visibleColumns.length + nLead} />
      ) : error ? (
        <div className="rounded-xl border border-[color:var(--color-rule)] bg-[color:var(--color-card)] p-12 text-center">
          <p className="text-sm text-[color:var(--color-priority-urgent)]">
            {typeof error === 'string' ? error : 'Something went wrong loading this table.'}
          </p>
          {onRetry ? (
            <button
              type="button"
              onClick={onRetry}
              className="mt-3 text-sm font-medium text-[color:var(--color-accent-strong)] hover:underline cursor-pointer"
            >
              Try Again
            </button>
          ) : null}
        </div>
      ) : pageRows.length === 0 ? (
        <EmptyState emptyState={emptyState} onClear={chips.length ? clearAll : null} />
      ) : (
        table
      )}
    </div>
  )

  return (
    <>
      {body}
      {drawerOpen ? (
        <FilterDrawer
          config={filterConfig}
          values={filterValues}
          previewCount={filterPreviewCount}
          onApply={setFilterValues}
          onClose={() => setDrawerOpen(false)}
        />
      ) : null}
      {exportOpen ? (
        <ExportModal
          visibleColumnCount={visibleColumns.filter((c) => c.exportable !== false).length}
          totalColumnCount={arranged.filter((c) => c.exportable !== false).length}
          pageCount={pageRows.length}
          totalCount={total}
          selectedCount={selected.size}
          onExport={handleExport}
          onClose={() => setExportOpen(false)}
          isoDate={isoDate || todayKey()}
        />
      ) : null}
      {colsOpen ? (
        <ColumnManager
          anchorRect={colsRect}
          // Right-pinned columns (e.g. the Actions kebab) are fixed to the
          // right edge and can't be reordered or hidden, so they're kept
          // out of the manager entirely — dragging one there did nothing
          // in the table, which read as broken. They're re-appended at the
          // end automatically by useTableColumns' ordering.
          columns={arranged.filter((c) => !c.pinnedRight)}
          onChangeOrder={setOrder}
          onChangeHidden={setHidden}
          onTogglePin={onTogglePin}
          onReset={resetColumns}
          onClose={() => setColsOpen(false)}
        />
      ) : null}
    </>
  )
}

/* ============================ default cell + states ============================ */

function formatCell(v) {
  if (v == null || v === '')
    return <span className="text-[color:var(--color-ink-subtle)]">—</span>
  return String(v)
}

function EmptyState({ emptyState, onClear }) {
  const Icon = emptyState?.icon
  return (
    <div className="rounded-2xl border border-dashed border-[color:var(--color-rule-strong)] bg-[color:var(--color-card)] p-14 text-center">
      {Icon ? (
        <span className="mx-auto mb-3 inline-flex h-14 w-14 items-center justify-center rounded-2xl bg-[color:var(--color-accent-bg)] text-[color:var(--color-accent-strong)] border border-[color:var(--color-accent-border)]">
          <Icon size={26} />
        </span>
      ) : null}
      <p className="text-base font-semibold text-[color:var(--color-ink-strong)]">
        {emptyState?.title ?? 'No records found'}
      </p>
      {emptyState?.body ? (
        <p className="mt-1.5 text-sm text-[color:var(--color-ink-muted)] max-w-md mx-auto leading-relaxed">
          {emptyState.body}
        </p>
      ) : null}
      {onClear ? (
        <button
          type="button"
          onClick={onClear}
          className="mt-4 inline-flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-medium text-[color:var(--color-accent-strong)] hover:bg-[color:var(--color-accent-bg)] cursor-pointer"
        >
          Clear All Filters
        </button>
      ) : null}
    </div>
  )
}

function SkeletonTable({ rows = 6, cols = 6 }) {
  return (
    <div className="rounded-xl border border-[color:var(--color-rule)] bg-[color:var(--color-card)] overflow-hidden shadow-sm">
      <div className="divide-y divide-[color:var(--color-rule)]">
        {Array.from({ length: rows }).map((_, i) => (
          <div key={i} className="flex items-center gap-4 px-4 py-3.5 animate-pulse">
            {Array.from({ length: Math.min(cols, 7) }).map((_, j) => (
              <div
                key={j}
                className="h-3 rounded bg-[color:var(--color-rule)]"
                style={{ width: j === 0 ? 24 : `${8 + ((i + j) % 4) * 4}%` }}
              />
            ))}
          </div>
        ))}
      </div>
    </div>
  )
}
